/**
 * UnifiedCustomerVehicleSearch — one search input for the work-order create
 * flow (#1002).
 *
 * The user types a person name, a company name or a registration number into
 * a single box; persons, companies and vehicles are searched simultaneously
 * and shown as grouped results. Create actions are ALWAYS appended below the
 * results (a namesake may require creating a duplicate-named person), ordered
 * by an input-shape heuristic that only RANKS options — it never hides them.
 *
 * Plate-like input triggers the external registry lookup in parallel with
 * the local vehicle search (#562, #2111); the result is previewed on the
 * "create new vehicle" action row, so plain Enter continues into the
 * prefilled inline vehicle form. Create-action rows are shown as soon as
 * the input is long enough — they do not wait on entity fetches. Enter
 * never creates anything without a visible, selectable row.
 *
 * The input serves two slots (orderer + vehicle) in either order: `mode`
 * narrows what is searched once one slot is filled. The party-first flow keeps
 * the #939 behaviour — with an orderer selected and the box empty, the
 * orderer's own vehicles are listed up-front.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import type { PartyRef } from '@/components/parties/PartyPicker';
import { usePersons, type Person } from '@/hooks/usePersons';
import { useCompanies, type Company } from '@/hooks/useCompanies';
import { useVehicles, type VehicleListItem } from '@/hooks/useVehicles';
import { useVehicleLookup, type VehicleLookupResult } from '@/hooks/useVehicleLookup';
import { useDebounce } from '@/hooks/useDebounce';
import { ApiError } from '@/lib/api';
import { isTemporarilyUnavailable } from '@/lib/describeApiError';

// --- Input-shape heuristics (ranking only — never used to hide options) ---

/** Normalise a plate: drop everything but letters/digits, upper-case. */
export function normalizePlate(input: string): string {
  return input.replace(/[^a-zA-Z0-9åäöÅÄÖ]/g, '').toUpperCase();
}

/**
 * Loose plate detector: 2–8 normalised characters containing at least one
 * letter AND one digit. Covers standard Finnish plates (ABC-123) and
 * personalised ones (JASKA-1) while rejecting names and business ids.
 */
export function isPlateLike(input: string): boolean {
  const normalized = normalizePlate(input.trim());
  if (normalized.length < 2 || normalized.length > 8) return false;
  return /[A-ZÅÄÖ]/.test(normalized) && /\d/.test(normalized);
}

/**
 * Canonical display/storage form for a plate: letter and digit groups joined
 * with a dash (ABC123 → ABC-123), matching the Finnish convention used across
 * the vehicle registry. Falls back to the normalised form for shapes that
 * don't split cleanly (e.g. personalised plates).
 */
export function formatPlate(input: string): string {
  const normalized = normalizePlate(input);
  const match = /^([A-ZÅÄÖ]{1,3})(\d{1,3})$/.exec(normalized);
  return match ? `${match[1]}-${match[2]}` : normalized;
}

/** Finnish business id: 1234567-8. */
export function isBusinessIdLike(input: string): boolean {
  return /^\d{7}-\d$/.test(input.trim());
}

/** Company-name detector: legal-form suffix on the last word, or a business id. */
export function looksLikeCompany(input: string): boolean {
  if (isBusinessIdLike(input)) return true;
  const words = input.trim().split(/\s+/);
  const last = words[words.length - 1] ?? '';
  return /^(oy|oyj|ab|ky|tmi|ry|osk|oü|gmbh|ltd|inc|ag)\.?$/i.test(last);
}

/** Split typed text into first/last name for the inline person prefill. */
export function parsePersonName(input: string): { firstName: string; lastName: string } {
  const words = input.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: words[0] ?? '',
    lastName: words.slice(1).join(' '),
  };
}

// --- Option model ---

export type SearchMode = 'both' | 'vehicleOnly' | 'partyOnly';

type UnifiedOption =
  | { kind: 'vehicle'; vehicle: VehicleListItem }
  | { kind: 'person'; person: Person }
  | { kind: 'company'; company: Company }
  | { kind: 'createVehicle'; reg: string }
  | { kind: 'createPerson'; input: string }
  | { kind: 'createCompany'; input: string };

type GroupKey = 'vehicles' | 'persons' | 'companies' | 'actions';

function optionGroup(option: UnifiedOption): GroupKey {
  switch (option.kind) {
    case 'vehicle':
      return 'vehicles';
    case 'person':
      return 'persons';
    case 'company':
      return 'companies';
    default:
      return 'actions';
  }
}

function optionKey(option: UnifiedOption): string {
  switch (option.kind) {
    case 'vehicle':
      return `v-${option.vehicle.linkId ?? option.vehicle.id}`;
    case 'person':
      return `p-${option.person.id}`;
    case 'company':
      return `c-${option.company.id}`;
    case 'createVehicle':
      return 'a-vehicle';
    case 'createPerson':
      return 'a-person';
    case 'createCompany':
      return 'a-company';
  }
}

function optionLabel(option: UnifiedOption): string {
  switch (option.kind) {
    case 'vehicle':
      return option.vehicle.registrationNumber;
    case 'person':
      return `${option.person.firstName} ${option.person.lastName}`.trim();
    case 'company':
      return option.company.name;
    case 'createVehicle':
      return option.reg;
    case 'createPerson':
    case 'createCompany':
      return option.input;
  }
}

// Group headers via the labelKey pattern — resolved with the right t() inside
// the component ('actions' lives in the workorders namespace, the rest in common).
const GROUP_LABEL_KEYS: Record<GroupKey, { ns: 'common' | 'workorders'; key: string }> = {
  vehicles: { ns: 'common', key: 'searchCategories.vehicles' },
  persons: { ns: 'common', key: 'searchCategories.persons' },
  companies: { ns: 'common', key: 'searchCategories.companies' },
  actions: { ns: 'workorders', key: 'create.createNewGroup' },
};

/** Minimum normalised plate length before the external registry lookup fires. */
const MIN_LOOKUP_LENGTH = 3;

type LookupErrorKey = 'lookup.notConfiguredDesc' | 'lookup.notFoundDesc' | 'lookup.failedDesc';

function lookupErrorKey(error: unknown): LookupErrorKey {
  if (isTemporarilyUnavailable(error)) return 'lookup.failedDesc';
  if (error instanceof ApiError && error.status === 503) return 'lookup.notConfiguredDesc';
  if (error instanceof ApiError && error.status === 404) return 'lookup.notFoundDesc';
  const message = error instanceof Error ? error.message : '';
  if (/not configured/i.test(message)) return 'lookup.notConfiguredDesc';
  return 'lookup.failedDesc';
}

interface UnifiedCustomerVehicleSearchProps {
  mode: SearchMode;
  inputValue: string;
  onInputChange: (value: string) => void;
  /** Selected orderer — drives the party-vehicles listing in vehicleOnly mode (#939). */
  ordererParty: PartyRef | null;
  onVehicleSelect: (vehicle: VehicleListItem) => void;
  onPersonSelect: (person: Person) => void;
  onCompanySelect: (company: Company) => void;
  onCreateVehicle: (reg: string, lookup: VehicleLookupResult | null) => void;
  onCreatePerson: (input: string) => void;
  onCreateCompany: (input: string) => void;
  /** A registry lookup selected while still pending resolved after the fact. */
  onLookupResolved: (reg: string, lookup: VehicleLookupResult) => void;
  autoFocus?: boolean;
  /**
   * Pick the orderer's only vehicle without a click (#1113). The vehicle-change
   * panel (#3285) turns this off: there the sole vehicle is usually the one
   * already on the order, and auto-selecting it would close the panel.
   */
  autoSelectSoleVehicle?: boolean | undefined;
}

export function UnifiedCustomerVehicleSearch({
  mode,
  inputValue,
  onInputChange,
  ordererParty,
  onVehicleSelect,
  onPersonSelect,
  onCompanySelect,
  onCreateVehicle,
  onCreatePerson,
  onCreateCompany,
  onLookupResolved,
  autoFocus,
  autoSelectSoleVehicle = true,
}: UnifiedCustomerVehicleSearchProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const { t: tv } = useTranslation('vehicles');

  const debouncedInput = useDebounce(inputValue, 300);
  const q = debouncedInput.trim();
  const searchEnabled = q.length >= 2;

  const includeVehicles = mode !== 'partyOnly';
  const includeParties = mode !== 'vehicleOnly';

  const plateLike = isPlateLike(q);
  const liveQ = inputValue.trim();
  const livePlateLike = isPlateLike(liveQ);
  const liveCompanyLike = looksLikeCompany(liveQ);

  // --- Entity searches (debounced; react-query cancels/keys out stale responses) ---
  const vehiclesQuery = useVehicles(
    { q, limit: 10 },
    { enabled: searchEnabled && includeVehicles },
  );
  const personsQuery = usePersons(
    { q, limit: 5 },
    { enabled: searchEnabled && includeParties },
  );
  const companiesQuery = useCompanies(
    { q, limit: 5 },
    { enabled: searchEnabled && includeParties },
  );

  // Party-first flow (#939): with an orderer chosen and the box empty, offer
  // the orderer's own vehicles up-front.
  const partyVehiclesQuery = useVehicles(
    ordererParty
      ? ordererParty.partyType === 'person'
        ? { personId: ordererParty.personId ?? undefined, limit: 50 }
        : { companyId: ordererParty.companyId ?? undefined, limit: 50 }
      : {},
    { enabled: mode === 'vehicleOnly' && !!ordererParty },
  );

  const vehicleResults = useMemo((): VehicleListItem[] => {
    if (searchEnabled && includeVehicles) {
      return vehiclesQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [];
    }
    if (!searchEnabled && mode === 'vehicleOnly' && ordererParty) {
      const rows = partyVehiclesQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [];
      const byId = new Map<string, VehicleListItem>();
      for (const v of rows) {
        if (!byId.has(v.id)) byId.set(v.id, v);
      }
      return [...byId.values()];
    }
    return [];
  }, [searchEnabled, includeVehicles, mode, ordererParty, vehiclesQuery.data, partyVehiclesQuery.data]);

  // Auto-select the orderer's sole vehicle (#1113) — nothing to choose
  // between, so skip the extra click. Once selected, the parent resolves
  // the vehicle slot and this component unmounts (searchMode becomes null),
  // so this can't loop.
  useEffect(() => {
    const soleVehicle = vehicleResults.length === 1 ? vehicleResults[0] : undefined;
    if (
      autoSelectSoleVehicle &&
      mode === 'vehicleOnly' &&
      ordererParty &&
      !searchEnabled &&
      !partyVehiclesQuery.isFetching &&
      soleVehicle
    ) {
      onVehicleSelect(soleVehicle);
    }
  }, [
    autoSelectSoleVehicle,
    mode,
    ordererParty,
    searchEnabled,
    partyVehiclesQuery.isFetching,
    vehicleResults,
    onVehicleSelect,
  ]);

  const personResults = useMemo(
    (): Person[] =>
      searchEnabled && includeParties
        ? (personsQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [])
        : [],
    [searchEnabled, includeParties, personsQuery.data],
  );
  const companyResults = useMemo(
    (): Company[] =>
      searchEnabled && includeParties
        ? (companiesQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [])
        : [],
    [searchEnabled, includeParties, companiesQuery.data],
  );

  // --- External registry lookup (auto on zero local plate hits, #562) ---
  const lookupMutation = useVehicleLookup();
  const [lookupPreview, setLookupPreview] = useState<VehicleLookupResult | null>(null);
  const [lookupTriedFor, setLookupTriedFor] = useState<string | null>(null);
  const [lookupError, setLookupError] = useState<LookupErrorKey | null>(null);
  // Latest debounced query — lets late lookup responses detect staleness.
  const qRef = useRef(q);
  qRef.current = q;
  // Set when the user selects "create vehicle" while the lookup is in flight.
  const pendingCreateRegRef = useRef<string | null>(null);
  const vehicleResultsRef = useRef(vehicleResults);
  vehicleResultsRef.current = vehicleResults;

  // Reset the preview whenever the (debounced) query changes.
  useEffect(() => {
    setLookupPreview(null);
    setLookupTriedFor(null);
    setLookupError(null);
  }, [q]);

  useEffect(() => {
    const shouldLookup =
      includeVehicles &&
      searchEnabled &&
      plateLike &&
      normalizePlate(q).length >= MIN_LOOKUP_LENGTH &&
      vehicleResults.length === 0 &&
      lookupTriedFor !== q &&
      !lookupMutation.isPending;

    if (!shouldLookup) return;

    setLookupTriedFor(q);
    setLookupError(null);
    const firedReg = q;
    lookupMutation.mutate(
      { registrationNumber: normalizePlate(firedReg) },
      {
        onSuccess: (data) => {
          if (
            pendingCreateRegRef.current &&
            normalizePlate(pendingCreateRegRef.current) === normalizePlate(firedReg)
          ) {
            // The user already continued into the create form — merge there.
            pendingCreateRegRef.current = null;
            onLookupResolved(firedReg, data);
          } else if (
            normalizePlate(qRef.current) === normalizePlate(firedReg) &&
            vehicleResultsRef.current.length === 0
          ) {
            setLookupPreview(data);
          }
        },
        onError: (error) => {
          if (normalizePlate(qRef.current) !== normalizePlate(firedReg)) return;
          setLookupError(lookupErrorKey(error));
        },
      },
    );
  }, [
    includeVehicles, searchEnabled, plateLike, q, vehicleResults.length,
    lookupTriedFor, lookupMutation, onLookupResolved,
  ]);

  const debouncePending = liveQ !== q;
  const queriesPending =
    searchEnabled &&
    ((includeVehicles && vehiclesQuery.isFetching) ||
      (includeParties && (personsQuery.isFetching || companiesQuery.isFetching)));

  // Entity hits are keyed by the debounced `q`. While the typed value is
  // ahead of `q`, those hits belong to the previous query; showing them
  // would let MUI autoHighlight keep a stale row (matched by equal label)
  // so Enter selects the previous result. Create-action rows are built from
  // the live input and do not depend on fetches, so the menu stays usable.
  // `value` is always null, so when hits for the new `q` append, highlight
  // either follows a matching label (e.g. the local vehicle for this plate)
  // or resets to the first option — never a leftover from the previous q.
  const showEntityHits = !debouncePending;

  // --- Assemble grouped options; heuristic decides ORDER only ---
  const options = useMemo((): UnifiedOption[] => {
    const vehicleOptions: UnifiedOption[] = showEntityHits
      ? vehicleResults.map((vehicle) => ({ kind: 'vehicle', vehicle }))
      : [];
    const personOptions: UnifiedOption[] = showEntityHits
      ? personResults.map((person) => ({ kind: 'person', person }))
      : [];
    const companyOptions: UnifiedOption[] = showEntityHits
      ? companyResults.map((company) => ({ kind: 'company', company }))
      : [];

    const groups = livePlateLike
      ? [vehicleOptions, personOptions, companyOptions]
      : liveCompanyLike
        ? [companyOptions, personOptions, vehicleOptions]
        : [personOptions, companyOptions, vehicleOptions];

    const actions: UnifiedOption[] = [];
    if (liveQ.length >= 2) {
      // "Create vehicle" only appears for plate-like input (decision #1002);
      // person/company create actions are always offered, ranked by confidence.
      if (includeVehicles && livePlateLike) {
        actions.push({ kind: 'createVehicle', reg: formatPlate(liveQ) });
      }
      if (includeParties) {
        const partyActions: UnifiedOption[] = liveCompanyLike
          ? [{ kind: 'createCompany', input: liveQ }, { kind: 'createPerson', input: liveQ }]
          : [{ kind: 'createPerson', input: liveQ }, { kind: 'createCompany', input: liveQ }];
        actions.push(...partyActions);
      }
    }

    return [...groups.flat(), ...actions];
  }, [
    showEntityHits, vehicleResults, personResults, companyResults,
    livePlateLike, liveCompanyLike, liveQ, includeVehicles, includeParties,
  ]);

  const isSearching =
    (inputValue.trim().length >= 2 && (debouncePending || queriesPending)) ||
    (mode === 'vehicleOnly' && !searchEnabled && partyVehiclesQuery.isFetching);

  const handleSelect = (_event: React.SyntheticEvent, value: UnifiedOption | null) => {
    if (!value) return;
    switch (value.kind) {
      case 'vehicle':
        onVehicleSelect(value.vehicle);
        break;
      case 'person':
        onPersonSelect(value.person);
        break;
      case 'company':
        onCompanySelect(value.company);
        break;
      case 'createVehicle':
        if (lookupPreview) {
          onCreateVehicle(value.reg, lookupPreview);
        } else {
          if (lookupMutation.isPending) {
            pendingCreateRegRef.current = value.reg;
          }
          onCreateVehicle(value.reg, null);
        }
        break;
      case 'createPerson':
        onCreatePerson(value.input);
        break;
      case 'createCompany':
        onCreateCompany(value.input);
        break;
    }
    onInputChange('');
  };

  const label =
    mode === 'vehicleOnly'
      ? t('create.searchVehicleLabel')
      : mode === 'partyOnly'
        ? t('create.unifiedSearchPartyLabel')
        : t('create.unifiedSearchLabel');
  const placeholder =
    mode === 'vehicleOnly'
      ? t('create.searchVehiclePlaceholder')
      : mode === 'partyOnly'
        ? t('create.unifiedSearchPartyPlaceholder')
        : t('create.unifiedSearchPlaceholder');

  return (
    <Autocomplete<UnifiedOption>
      options={options}
      value={null}
      onChange={handleSelect}
      inputValue={inputValue}
      onInputChange={(_e, value) => onInputChange(value)}
      // `value` is always null, so MUI's default clearOnBlur (true when not
      // freeSolo) would wipe the typed text — usually a plate — the moment
      // focus leaves the box: clicking "create new vehicle", a step header or
      // any other field (#2883). The text is the user's, not a stale label.
      clearOnBlur={false}
      loading={isSearching}
      loadingText={t('create.vehicleSearching')}
      autoHighlight
      openOnFocus
      filterOptions={(x) => x}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(option, value) => optionKey(option) === optionKey(value)}
      groupBy={(option) => optionGroup(option)}
      noOptionsText={
        !searchEnabled
          ? mode === 'vehicleOnly' && ordererParty
            ? t('create.partyNoVehicles')
            : tc('searchCategories.minChars')
          : t('create.noPartiesFound')
      }
      renderGroup={(params) => {
        const labelDef = GROUP_LABEL_KEYS[params.group as GroupKey];
        const groupLabel = labelDef ? (labelDef.ns === 'common' ? tc(labelDef.key) : t(labelDef.key)) : params.group;
        return (
          <li key={params.key}>
            <Typography
              component="div"
              variant="caption"
              sx={{
                px: 2,
                py: 0.5,
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              {groupLabel}
            </Typography>
            <Box component="ul" sx={{ p: 0 }}>
              {params.children}
            </Box>
          </li>
        );
      }}
      renderOption={(props, option) => {
        const { key: _key, ...optionProps } = props as { key?: string } & React.HTMLAttributes<HTMLLIElement>;
        switch (option.kind) {
          case 'vehicle': {
            const v = option.vehicle;
            return (
              <li {...optionProps} key={optionKey(option)}>
                <Box sx={{ py: 0.5, width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DirectionsCarIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" fontWeight={600}>
                      {v.registrationNumber}
                    </Typography>
                    {(v.make || v.model) && (
                      <Typography variant="body2" color="text.secondary">
                        {[v.make, v.model, v.year].filter(Boolean).join(' ')}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, pl: 3.5 }}>
                    {v.customerRole && (
                      <Chip
                        label={tv(`roles.${v.customerRole}`)}
                        size="small"
                        variant="outlined"
                        color={v.customerRole === 'owner' ? 'primary' : v.customerRole === 'holder' ? 'secondary' : 'default'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {v.customerName}
                      {v.customerPhone ? ` · ${v.customerPhone}` : ''}
                    </Typography>
                  </Box>
                </Box>
              </li>
            );
          }
          case 'person': {
            const p = option.person;
            return (
              <li {...optionProps} key={optionKey(option)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2">{p.firstName} {p.lastName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[p.phone, p.email].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                </Box>
              </li>
            );
          }
          case 'company': {
            const c = option.company;
            return (
              <li {...optionProps} key={optionKey(option)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <BusinessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2">{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[c.businessId, c.phone].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                </Box>
              </li>
            );
          }
          case 'createVehicle': {
            const showLookupOnCreateRow = !debouncePending && vehicleResults.length === 0;
            return (
              <li {...optionProps} key={optionKey(option)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <AddIcon fontSize="small" color="primary" />
                  <Box>
                    <Typography variant="body2" color="primary">
                      {t('create.createVehicleAction', { reg: option.reg })}
                    </Typography>
                    {showLookupOnCreateRow && lookupMutation.isPending ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CircularProgress size={12} />
                        <Typography variant="caption" color="text.secondary">
                          {t('create.registryLookupPending')}
                        </Typography>
                      </Box>
                    ) : showLookupOnCreateRow && lookupPreview ? (
                      <Typography variant="caption" color="text.secondary">
                        {t('create.registryLookupPreview', {
                          description: [lookupPreview.make, lookupPreview.model, lookupPreview.year]
                            .filter(Boolean)
                            .join(' '),
                        })}
                      </Typography>
                    ) : showLookupOnCreateRow && lookupError ? (
                      <Typography variant="caption" color="text.secondary">
                        {tv(lookupError)}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              </li>
            );
          }
          case 'createPerson':
            return (
              <li {...optionProps} key={optionKey(option)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <AddIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="primary">
                    {t('create.createPersonAction', { name: option.input })}
                  </Typography>
                </Box>
              </li>
            );
          case 'createCompany':
            return (
              <li {...optionProps} key={optionKey(option)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <AddIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="primary">
                    {t('create.createCompanyAction', { name: option.input })}
                  </Typography>
                </Box>
              </li>
            );
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          autoFocus={autoFocus}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isSearching || lookupMutation.isPending ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
